import React, { useEffect, useState } from 'react';
import { Frame, Navigation, TopBar, Select } from '@shopify/polaris';
import { HomeIcon, StarIcon, SettingsIcon, CreditCardIcon, ProductIcon, CodeIcon } from '@shopify/polaris-icons';
import crownWhite from '@/assets/icons/crown-white.png';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isLocalDevelopment } from '../utils/env';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t, i18n: i18nInstance } = useTranslation();
  const { shopId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPaid, setIsPaid] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(i18nInstance.language || 'en-US');

  // Verificar status da assinatura
  useEffect(() => {
    const checkSubscription = async () => {
      if (!shopId || isLocalDevelopment()) {
        return;
      }

      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
        
        const response = await fetch(
          `${supabaseUrl}/functions/v1/billing?shopId=${shopId}`,
          {
            headers: {
              'apikey': supabaseAnonKey,
              'Authorization': `Bearer ${supabaseAnonKey}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setIsPaid(data.plan === 'paid' && data.status === 'active');
        }
      } catch (err) {
        // Em caso de erro, assumir plano free
        setIsPaid(false);
      }
    };

    checkSubscription();
  }, [shopId]);

  const navigationItems = [
    {
      label: t('navigation.dashboard', { defaultValue: 'Dashboard' }),
      url: '/',
      icon: HomeIcon,
    },
    {
      label: t('navigation.products', { defaultValue: 'Produtos' }),
      url: '/products',
      icon: ProductIcon,
    },
    {
      label: t('navigation.reviews', { defaultValue: 'Avaliações' }),
      url: '/reviews',
      icon: StarIcon,
    },
    {
      label: t('navigation.themeSetup', { defaultValue: 'Configurar Tema' }),
      url: '/theme-setup',
      icon: CodeIcon,
    },
    {
      label: t('navigation.billing', { defaultValue: 'Assinatura' }),
      url: '/billing',
      icon: CreditCardIcon,
    },
    {
      label: t('navigation.settings', { defaultValue: 'Configurações' }),
      url: '/settings',
      icon: SettingsIcon,
    },
  ];

  const handleNavigation = (url: string) => {
    navigate(url);
  };

  // Determinar qual item está ativo baseado na rota atual
  const getActiveUrl = () => {
    const path = location.pathname;
    
    // IMPORTANTE: Dashboard só deve ser ativo se for exatamente "/"
    // Verificar isso PRIMEIRO antes de qualquer outra coisa
    if (path === '/') {
      return '/';
    }
    
    // Para outras rotas, verificar match exato primeiro
    const exactMatch = navigationItems
      .filter(item => item.url !== '/') // Excluir dashboard
      .find(item => item.url === path);
    
    if (exactMatch) {
      return exactMatch.url;
    }
    
    // Verificar sub-rotas (ex: /billing/confirm deve ativar /billing)
    const subRouteMatch = navigationItems
      .filter(item => item.url !== '/') // Excluir dashboard
      .find(item => path.startsWith(item.url + '/'));
    
    if (subRouteMatch) {
      return subRouteMatch.url;
    }
    
    // Se não houver match, retornar path vazio para não marcar nada
    return '';
  };

  const activeUrl = getActiveUrl();

  const languageOptions = [
    { label: 'English', value: 'en-US' },
    { label: 'Português (Brasil)', value: 'pt-BR' },
    { label: 'Español', value: 'es-ES' },
    { label: 'Français', value: 'fr-FR' },
    { label: 'Deutsch', value: 'de-DE' },
  ];

  const handleLanguageChange = (value: string) => {
    setSelectedLanguage(value);
    i18nInstance.changeLanguage(value);
    // Salvar preferência no localStorage
    localStorage.setItem('i18nextLng', value);
  };

  return (
    <Frame
      navigation={
        <Navigation location={activeUrl || location.pathname}>
          <Navigation.Section
            items={navigationItems.map((item) => {
              // Determinar se o item está ativo
              const isActive = (() => {
                const currentPath = location.pathname;
                
                // Dashboard só é ativo se for exatamente "/"
                if (item.url === '/') {
                  return currentPath === '/';
                }
                
                // Outros itens: match exato ou sub-rota
                return currentPath === item.url || currentPath.startsWith(item.url + '/');
              })();
              
              return {
                url: item.url,
                label: item.label,
                icon: item.icon,
                onClick: () => handleNavigation(item.url),
                selected: isActive,
              };
            })}
          />
        </Navigation>
      }
      topBar={
        <TopBar 
          showNavigationToggle
          secondaryMenu={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Select
                label=""
                labelHidden
                options={languageOptions}
                value={selectedLanguage}
                onChange={handleLanguageChange}
              />
              {isPaid && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  borderRadius: '20px',
                  boxShadow: '0 2px 8px rgba(255, 215, 0, 0.4)',
                  border: '1px solid rgba(255, 215, 0, 0.6)',
                }}>
                  <img 
                    src={crownWhite} 
                    alt="PRO" 
                    style={{
                      width: '20px',
                      height: '20px',
                      objectFit: 'contain',
                    }}
                  />
                  <span style={{
                    color: '#000',
                    fontWeight: '700',
                    fontSize: '13px',
                    letterSpacing: '0.5px',
                    textShadow: '0 1px 2px rgba(255, 255, 255, 0.3)',
                  }}>
                    PRO
                  </span>
                </div>
              )}
            </div>
          }
        />
      }
    >
      <style>{`
        /* Aumentar fonte dos itens do menu */
        nav[aria-label*="Navigation"] a,
        nav[aria-label*="Navigation"] button {
          font-size: 15px !important;
          font-weight: 500 !important;
        }
        
        nav[aria-label*="Navigation"] a[aria-current="page"],
        nav[aria-label*="Navigation"] button[aria-current="page"] {
          font-weight: 600 !important;
        }
      `}</style>
      
      {/* Chat Intercom/Crisp - Adicionar quando tiver credenciais */}
      {/* 
      <script
        dangerouslySetInnerHTML={{
          __html: `
            // Intercom - Substitua YOUR_APP_ID pelo ID do Intercom
            (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/YOUR_APP_ID';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
            
            // OU Crisp (mais simples) - Substitua YOUR_WEBSITE_ID
            // window.$crisp=[];window.CRISP_WEBSITE_ID="YOUR_WEBSITE_ID";(function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();
          `,
        }}
      />
      */}
      
      {children}
    </Frame>
  );
};

export default Layout;
